import React, { useCallback } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Form from "antd/es/form";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import Organization from "@mapmycustomers/shared/types/Organization";
import Setting from "@mapmycustomers/shared/types/Setting";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, Popconfirm } from "@mapmycustomers/ui";

import { getMe, getOrganizationId, getUserSetting, isSettingUpdating } from "@app/store/iam";
import { updateSetting } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import Iam from "@app/types/Iam";
import NotificationSettingValue from "@app/types/notifications/NotificationSetting";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";

import SettingPage from "../common/SettingPage";

import Mentions from "./component/Mentions";
import NotesActivities from "./component/NotesActivities";
import RecordUpdates from "./component/RecordUpdates";
import Sharing from "./component/Sharing";
import TeamMentions from "./component/TeamMentions";
import styles from "./NotificationsCommon.module.scss";

interface Props {
  me: Iam;
  organizationId: Organization["id"];
  setting: Setting;
  updateSetting: typeof updateSetting.request;
  updating: boolean;
}

const Notifications: React.FC<Props> = ({
  me,
  organizationId,
  setting,
  updateSetting,
  updating,
}) => {
  const intl = useIntl();
  const analyticsIssuer = useExtendedAnalytics(["Settings", "Notificaions"]);
  const [form] = Form.useForm<NotificationSettingValue>();
  const [isDirty, setDirty, unsetDirty] = useBoolean();
  const [toggling, toggle, stopToggling] = useBoolean();

  const handleSubmit = useCallback(async () => {
    const values = form.getFieldsValue();
    updateSetting({
      callback: () => unsetDirty(),
      orgId: organizationId,
      setting: { ...setting, value: { ...values, enabled: true } },
      userId: me.id,
    });
  }, [form, me.id, organizationId, setting, unsetDirty, updateSetting]);

  const handleToggle = useCallback(
    (enabled: boolean) => {
      analyticsIssuer.clicked([`Opt ${enabled ? "in" : "out"} of Notifications`]);
      toggle();
      const values = form.getFieldsValue();
      updateSetting({
        callback: () => {
          stopToggling();
        },
        orgId: organizationId,
        setting: { ...setting, value: { ...values, enabled } },
        userId: me.id,
      });
    },
    [analyticsIssuer, form, me.id, organizationId, setting, stopToggling, toggle, updateSetting]
  );

  const handleUnDo = useCallback(() => {
    form.resetFields();
    unsetDirty();
  }, [form, unsetDirty]);

  const handleFormChanged = useCallback(
    (fields) => {
      if (fields.mentions !== undefined) {
        analyticsIssuer.clicked([`Toggle Mentions`]);
      }
      setDirty();
    },
    [analyticsIssuer, setDirty]
  );

  const handleToggleGroup = useCallback(
    (optionName: string) => {
      analyticsIssuer.clicked([`Toggle ${optionName}`]);
      setDirty();
    },
    [analyticsIssuer, setDirty]
  );

  return (
    <SettingPage
      changesSaving={updating}
      hasChanges={isDirty}
      onSaveChanges={form.submit}
      onUnDo={handleUnDo}
      title={intl.formatMessage({
        id: "settings.notifications.title",
        defaultMessage: "Notifications",
        description: "Title of settings notifications page",
      })}
    >
      <div className={styles.container}>
        <Form
          form={form}
          hidden={!setting.value.enabled}
          initialValues={setting.value}
          onChange={handleFormChanged}
          onFieldsChange={handleFormChanged}
          onFinish={handleSubmit}
          onValuesChange={handleFormChanged}
        >
          <div className={styles.formContainer}>
            <div className={styles.mentions}>
              <Mentions />
              <TeamMentions />
            </div>
            <RecordUpdates form={form} onToggle={() => handleToggleGroup("Record Updates")} />
            <Row gutter={layout.spacerL}>
              <Col span={12}>
                <NotesActivities
                  form={form}
                  onToggle={() => handleToggleGroup("Notes & Activities")}
                />
              </Col>
              <Col span={12}>
                <Sharing form={form} onToggle={() => handleToggleGroup("Sharing")} />
              </Col>
            </Row>
            <Row>
              <Col span={6}>
                <Popconfirm
                  cancelText={intl.formatMessage({
                    id: "settings.notifications.optOut.cancel",
                    defaultMessage: "Wait",
                    description: "Settings notifications - Opt-out of email notifications - Cancel",
                  })}
                  description={intl.formatMessage({
                    id: "settings.notifications.optOut.text",
                    defaultMessage: "Are you sure you want to opt-out of all notifications?",
                    description: "Settings notifications - Opt-out of email notifications text",
                  })}
                  okText={intl.formatMessage({
                    id: "settings.notifications.optOut.ok",
                    defaultMessage: "Opt out",
                    description: "Settings notifications - Opt-out of email notifications - OK",
                  })}
                  onConfirm={() => !toggling && handleToggle(false)}
                  placement="bottom"
                >
                  <Button disabled={toggling} loading={toggling}>
                    {intl.formatMessage({
                      id: "settings.notifications.optOut",
                      defaultMessage: "Opt-out of email notifications",
                      description: "Settings notifications - Opt-out of email notifications",
                    })}
                  </Button>
                </Popconfirm>
              </Col>
            </Row>
          </div>
        </Form>
        {!setting.value.enabled && (
          <div className={styles.optInContainer}>
            <div className={styles.optDescription}>
              {intl.formatMessage({
                id: "settings.notifications.optIn.description",
                defaultMessage:
                  "Opt-in to receive notifications to your email. After opting - in, you can choose what type of notifications you’d like to recieve. To get push notifications sent to your mobile device, opt-in to notifications from the mobile app. ",
                description: "Settings notifications - Opt-in of email notifications - description",
              })}
            </div>
            <div>
              <Button
                disabled={toggling}
                loading={toggling}
                onClick={() => handleToggle(true)}
                type="primary"
              >
                {intl.formatMessage({
                  id: "settings.notifications.optIn",
                  defaultMessage: "Opt-in to Notifications",
                  description: "Settings notifications - Opt-in to Notifications",
                })}
              </Button>
            </div>
          </div>
        )}
      </div>
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  me: getMe(state)!,
  organizationId: getOrganizationId(state)!,
  setting: getUserSetting(state)("emailNotificationSettings")!,
  updating: isSettingUpdating(state)!,
});

const mapDispatchToProps = {
  updateSetting: updateSetting.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Notifications);
